
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  BroadcastMessage,
  fetchBroadcastMessages,
  fetchReadBroadcastIds,
  markBroadcastsAsRead,
} from "@/services/broadcastService";
import { supabase } from "@/integrations/supabase/client";

export type { BroadcastMessage };

interface BroadcastNotificationsContextValue {
  broadcasts: BroadcastMessage[];
  unreadBroadcasts: BroadcastMessage[];
  unreadCount: number;
  loading: boolean;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

const BroadcastNotificationsContext =
  createContext<BroadcastNotificationsContextValue | null>(null);

export const BroadcastNotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [broadcasts, setBroadcasts] = useState<BroadcastMessage[]>([]);
  const [unread, setUnread] = useState<BroadcastMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBroadcasts = useCallback(async () => {
    if (!user) {
      setBroadcasts([]);
      setUnread([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await fetchBroadcastMessages();

      if (error) {
        console.error("[BroadcastNotifications] fetch error:", error);
        setBroadcasts([]);
        setUnread([]);
        return;
      }

      setBroadcasts(data);

      if (data.length === 0) {
        setUnread([]);
        return;
      }

      const { data: readIds, error: readError } = await fetchReadBroadcastIds(user.id);

      if (readError) {
        console.error("[BroadcastNotifications] read status error:", readError);
        setUnread(data);
        return;
      }

      setUnread(data.filter((msg) => !readIds.includes(msg.id)));
    } catch (error) {
      console.error("[BroadcastNotifications] unexpected error:", error);
      setBroadcasts([]);
      setUnread([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchBroadcasts();
  }, [fetchBroadcasts]);

  // Rafraîchir à la reprise de focus (nouveau broadcast admin)
  useEffect(() => {
    const onFocus = () => {
      void fetchBroadcasts();
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchBroadcasts]);

  // Temps réel : nouveau message broadcast
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("broadcast_messages_changes")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "broadcast_messages" },
        () => {
          void fetchBroadcasts();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, fetchBroadcasts]);

  const markAllAsRead = useCallback(async () => {
    if (!user || unread.length === 0) return;

    const unreadIds = unread.map((msg) => msg.id);
    setUnread([]);

    const { error } = await markBroadcastsAsRead(user.id, unreadIds);

    if (error) {
      console.error("[BroadcastNotifications] mark as read error:", error);
      await fetchBroadcasts();
    }
  }, [user, unread, fetchBroadcasts]);

  const value = useMemo(
    () => ({
      broadcasts,
      unreadBroadcasts: unread,
      unreadCount: unread.length,
      loading,
      markAllAsRead,
      refresh: fetchBroadcasts,
    }),
    [broadcasts, unread, loading, markAllAsRead, fetchBroadcasts]
  );

  return (
    <BroadcastNotificationsContext.Provider value={value}>
      {children}
    </BroadcastNotificationsContext.Provider>
  );
};

export function useBroadcastNotifications() {
  const context = useContext(BroadcastNotificationsContext);

  if (!context) {
    throw new Error(
      "useBroadcastNotifications must be used within BroadcastNotificationsProvider"
    );
  }

  return context;
}
